import { createContext, useCallback, useContext, useEffect, useMemo, useState, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from 'react';

/**
 * Minimal client-side router (~1 KB). The app is a static single-page app, so all it needs is
 * history navigation, links and the current path and query. The API mirrors the small part of
 * Next.js the screens were written against: <Link>, useRouter, usePathname, useSearchParams.
 */
interface Location {
  pathname: string;
  search: string;
}

interface RouterState extends Location {
  navigate(to: string, opts?: { replace?: boolean }): void;
}

const Ctx = createContext<RouterState | null>(null);
let prefetcher: ((path: string) => void) | null = null;

/** Lets the route table warm a route's code when a link to it is hovered or touched. */
export function setPrefetcher(fn: (path: string) => void): void {
  prefetcher = fn;
}

const read = (): Location => ({ pathname: window.location.pathname, search: window.location.search });

export function RouterProvider({ children }: { children: ReactNode }) {
  const [loc, setLoc] = useState<Location>(read);
  useEffect(() => {
    const onPop = () => setLoc(read());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const navigate = useCallback((to: string, opts: { replace?: boolean } = {}) => {
    const url = new URL(to, window.location.href);
    if (url.origin !== window.location.origin) {
      window.location.assign(url.href);
      return;
    }
    window.history[opts.replace ? 'replaceState' : 'pushState'](null, '', url.pathname + url.search + url.hash);
    setLoc(read());
    if (!url.hash) window.scrollTo(0, 0);
  }, []);
  const value = useMemo(() => ({ ...loc, navigate }), [loc, navigate]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

function useRouterState(): RouterState {
  const v = useContext(Ctx);
  if (!v) throw new Error('router hooks need <RouterProvider>');
  return v;
}

export function usePathname(): string {
  return useRouterState().pathname;
}

export function useSearchParams(): URLSearchParams {
  const { search } = useRouterState();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export function useRouter() {
  const { navigate } = useRouterState();
  return useMemo(
    () => ({
      push: (to: string) => navigate(to),
      replace: (to: string) => navigate(to, { replace: true }),
      back: () => window.history.back(),
    }),
    [navigate],
  );
}

export interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string;
  replace?: boolean;
}

/** In-app link: navigates without a page load, but stays a real <a> (new tab, copy link, SEO). */
export function Link({ href, replace, onClick, onPointerEnter, onTouchStart, ...rest }: LinkProps) {
  const { navigate } = useRouterState();
  const handle = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || rest.target === '_blank') return;
    const url = new URL(href, window.location.href);
    if (url.origin !== window.location.origin) return;
    e.preventDefault();
    navigate(href, { replace });
  };
  const warm = () => prefetcher?.(new URL(href, window.location.href).pathname);
  return (
    <a
      href={href}
      onClick={handle}
      onPointerEnter={(e) => {
        onPointerEnter?.(e);
        warm();
      }}
      onTouchStart={(e) => {
        onTouchStart?.(e);
        warm();
      }}
      {...rest}
    />
  );
}
